import { useState } from 'react';
import {
  Typography,
  Card,
  Input,
  Select,
  Row,
  Col,
  Tag,
  Space,
  Table,
  Tooltip,
  Checkbox,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMatchRequests } from '@/hooks/useMatches';
import { SPORT_TYPE_CONFIG } from '@/config/constants';
import type { SportType } from '@/types/user';
import type { MatchRequestItem } from '@/api/matches.api';
import { UserLink } from '@/components/UserLink';

const { Title, Text } = Typography;

type Status = 'WAITING' | 'MATCHED' | 'CANCELLED' | 'EXPIRED';

const STATUS_CONFIG: Record<Status, { label: string; color: string }> = {
  WAITING: { label: '대기중', color: 'gold' },
  MATCHED: { label: '매칭됨', color: 'green' },
  CANCELLED: { label: '취소', color: 'red' },
  EXPIRED: { label: '만료', color: 'default' },
};

const TIME_SLOT_LABEL: Record<string, string> = {
  DAWN: '새벽 (00–03)',
  EARLY_MORNING: '이른 아침 (03–06)',
  MORNING: '아침 (06–09)',
  LATE_MORNING: '늦은 아침 (09–12)',
  AFTERNOON: '낮 (12–15)',
  LATE_AFTERNOON: '늦은 오후 (15–18)',
  EVENING: '저녁 (18–21)',
  NIGHT: '밤 (21–24)',
  ANY: '아무때나',
};

export function MatchRequestListPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<Status | undefined>();
  const [sportType, setSportType] = useState<SportType | undefined>();
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);

  // 전체 보기 OFF(기본): 대기중만 / ON: 전체(또는 상태 Select 로 세부 필터)
  const { data, isLoading } = useMatchRequests({
    search: search || undefined,
    status: showAll ? status : 'WAITING',
    sportType,
    page,
    pageSize: 20,
  });

  const columns: TableColumnsType<MatchRequestItem> = [
    {
      title: '요청 ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => (
        <Text copyable={{ text: id }} style={{ fontSize: 12, fontFamily: 'monospace' }}>
          {id.slice(0, 8)}…
        </Text>
      ),
      width: 120,
    },
    {
      title: '요청자',
      key: 'requester',
      render: (_, r) => (
        <UserLink userId={r.requester?.id}>{r.requester?.nickname ?? '-'}</UserLink>
      ),
      width: 140,
    },
    {
      title: '종목',
      dataIndex: 'sportType',
      key: 'sportType',
      render: (t: SportType) => (
        <Space>
          <span>{SPORT_TYPE_CONFIG[t]?.icon}</span>
          <span>{SPORT_TYPE_CONFIG[t]?.label ?? t}</span>
        </Space>
      ),
      width: 130,
    },
    {
      title: '핀',
      key: 'pin',
      render: (_, r) => r.pin?.name ?? r.locationName ?? '-',
      ellipsis: true,
    },
    {
      title: '희망 날짜',
      dataIndex: 'desiredDate',
      key: 'desiredDate',
      render: (d: string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '-'),
      width: 110,
    },
    {
      title: '희망 타임',
      dataIndex: 'desiredTimeSlot',
      key: 'desiredTimeSlot',
      render: (t: string | null) => (t ? TIME_SLOT_LABEL[t] ?? t : '-'),
      width: 140,
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      render: (s: Status) => {
        const cfg = STATUS_CONFIG[s] ?? { label: s, color: 'default' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
      width: 90,
    },
    {
      title: '캐주얼',
      dataIndex: 'isCasual',
      key: 'isCasual',
      render: (v: boolean) =>
        v ? <Tag color="cyan">캐주얼</Tag> : <Tag>경쟁</Tag>,
      width: 90,
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d: string) => dayjs(d).format('YYYY-MM-DD HH:mm'),
      width: 150,
    },
    {
      title: '만료',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (d: string) => (
        <Tooltip title={dayjs(d).format('YYYY-MM-DD HH:mm:ss')}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {d ? dayjs(d).format('MM-DD HH:mm') : '-'}
          </Text>
        </Tooltip>
      ),
      width: 120,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20,
          gap: 16,
        }}
      >
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            매칭 요청 관리
          </Title>
          <Text type="secondary">
            사용자가 올린 매칭 요청 현황 (어느 계정 / 어느 핀 / 몇시 타임 / 어떤 스포츠)
          </Text>
        </div>
        <Checkbox
          checked={showAll}
          onChange={(e) => {
            setShowAll(e.target.checked);
            setPage(1);
          }}
          style={{ whiteSpace: 'nowrap', marginTop: 4 }}
        >
          전체 상태 보기 (취소·만료·매칭됨 포함)
        </Checkbox>
      </div>

      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} lg={8}>
            <Input
              placeholder="닉네임 또는 핀 이름으로 검색"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Select
              placeholder="상태"
              style={{ width: '100%' }}
              allowClear
              disabled={!showAll}
              value={showAll ? status : 'WAITING'}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={Object.entries(STATUS_CONFIG).map(([value, { label }]) => ({
                value,
                label,
              }))}
            />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Select
              placeholder="종목"
              style={{ width: '100%' }}
              value={sportType ?? ''}
              onChange={(v) => {
                setSportType(v === '' ? undefined : (v as SportType));
                setPage(1);
              }}
              options={[
                { value: '', label: '전체' },
                ...Object.entries(SPORT_TYPE_CONFIG).map(([value, { label }]) => ({
                  value,
                  label,
                })),
              ]}
            />
          </Col>
        </Row>
      </Card>

      <Card style={{ borderRadius: 8 }}>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">총 {(data?.total ?? 0).toLocaleString()}건</Text>
        </div>

        <Table
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: 20,
            total: data?.total ?? 0,
            showTotal: (total) => `총 ${(total ?? 0).toLocaleString()}건`,
            onChange: setPage,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
}
